<template>
  <!-- eslint-disable vuejs-accessibility/alt-text -->

  <div v-if="prefersReducedMotion">
    <slot name="reduced-motion" />
  </div>
  <object
    v-else
    aria-hidden="true"
    :data="animationSrc || ''"
    type="image/svg+xml"
    @load="onSvgLoaded"
  />
  <!-- eslint-enable vuejs-accessibility/alt-text -->
</template>

<script lang="ts" setup>
export type AnimationPlayer = {
  pause: () => void
  play: () => void
  stop: () => void
}

type AnimationPlayerEmits = {
  (e: 'ready', player: AnimationPlayer): void
}

type AnimationPlayerProps = { name: string }

const emit = defineEmits<AnimationPlayerEmits>()

const { name } = defineProps<AnimationPlayerProps>()

const prefersReducedMotion = useReducedMotion()
const { getAnimation } = useAsset()

const player = ref<AnimationPlayer>()

const { data: animationSrc } = await useAsyncData(name, async () => await getAnimation(name), {
  watch: [() => name],
})

const pause = () => {
  if (!player.value) return
  player.value.pause()
}

const play = () => {
  if (!player.value) return
  player.value.play()
}

const stop = () => {
  if (!player.value) return
  player.value.stop()
}

const onSvgLoaded = (event: Event) => {
  const target = event.target as Maybe<HTMLObjectElement>

  const svgatorDocument = target?.contentDocument || target?.contentWindow?.document
  const svgatorPlayer = (
    svgatorDocument?.getElementsByTagName('svg')[0] as {
      svgatorPlayer?: AnimationPlayer
    }
  )?.svgatorPlayer

  if (!svgatorPlayer) return
  player.value = svgatorPlayer

  emit('ready', {
    pause,
    play,
    stop,
  })
}
</script>
